import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import React from 'react';

interface Agent {
  agent_enabler: string[];
  agent_name: string;
  agent_description: string;
}

interface AiSolutionData {
  benefits: Record<string, string>;
  complexity: string;
  created_at: string;
  data_sources: Record<string, any>;
  description: string;
  description_llm: string;
  diagram_id: string;
  id: number;
  impact: string;
  isActive: boolean;
  isDeleted: boolean;
  key_requirements: string[];
  llm_agent: Agent[];
  opportunity_name: string;
  roi: string;
  solution_enabler: string; // raw string from API
  updated_at: string;
}

interface CustomModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl';
  width?: string;
  height?: string;
  sx?: object;
  outerPadding?: number;
  titleActions?: React.ReactNode;
  agentResponse?: AiSolutionData | null;
}

const CustomModal: React.FC<CustomModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  maxWidth,
  width,
  sx,
  outerPadding,
  titleActions,
  agentResponse,
}) => {
  const solutions: string[] = agentResponse?.solution_enabler
    ? (() => {
        try {
          const parsed = JSON.parse(agentResponse.solution_enabler.replace(/'/g, '"'));
          // If it's already an array, return it
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          // If JSON.parse fails, treat it as a comma-separated string
          return agentResponse.solution_enabler
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
        }
      })()
    : [];

  return (
    <Dialog
      open={isOpen}
      onClose={() => {}}
      disableEscapeKeyDown
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: { xs: '0px', md: 2 },
          p: 0, // outerPadding moved inside content if needed
          maxWidth: { xs: '100%', md: maxWidth || 'lg' },
          width: { xs: '100%', md: width || 'auto' },
          height: 'auto',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          ...sx,
          pointerEvents: 'auto',
        },
      }}
    >
      {title && (
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            m: 2,
            position: 'sticky',
            top: 0,
            backgroundColor: '',
            color: '#ffffff',
            zIndex: 10,
            border: '1px solid #e0e0e0',
            flexDirection: 'row',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={onClose}
                //   sx={{ color: 'grey.500', '&:hover': { color: 'grey.700' } }}
                sx={{ color: '#ffffff', '&:hover': { color: 'grey.700' } }}
                aria-label="Close"
              >
                <img src="/images/arrow-left.svg" />
              </IconButton>
            </Box>
            {/* Left: Title */}
            {typeof title === 'string' ? (
              <Box>
                {(solutions?.length ?? 0) > 0 && (
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, fontSize: '12px', color: '#1B54F8' }}
                  >
                    {solutions?.join(', ')}
                  </Typography>
                )}

                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '20px',
                    color: '#000000',
                  }}
                >
                  {title}
                </Typography>
              </Box>
            ) : (
              title
            )}
          </Box>

          {/* Right: Actions + Close button */}
          {titleActions}
        </DialogTitle>
      )}

      <DialogContent
        sx={{
          p: outerPadding || 2,
          overflowY: 'auto',
          flex: 1,
        }}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};

export default CustomModal;
